import jQuery from '../utils/fl-jquery';
import FlUtil from '../utils/utils';
import SvgInjector from '../utils/svg-injector';
import frontend from './frontend';
import Request from './request';
import './jquery-ui-autocomplete';
import { analyticsTracker as Tracker, identity, logger } from '@findologic/js-common';
import ShopSystem from './shopsystem/shopsystem';
import { EventNames } from '../../tscoba/src/Common/enums.ts';

const utils = FlUtil;

const FlAutocomplete = {
    VERSION: 1.0,
    READY_EVENT_NAME: 'flAutocompleteReady',
    SUGGEST_AUTOCOMPLETE_BLOCK: 'suggest',
    SUGGESTION_ICON_CLASS: '.fl-autocomplete .fl-item-suggest .fl-suggestion-icon',

    /**
     * Number of milliseconds that have to pass between selecting Smart Suggest items in order for a
     * search to be triggered. This prevents accidental double-clicks from sending multiple requests.
     */
    INTERACTION_COOLDOWN_MS: 500,

    shopSystem: null,
    log: null,
    isDirectIntegration: false,
    usergrouphash: undefined,
    isMobile: undefined,
    preventInitialization: false,

    /**
     * Timestamp in milliseconds showing when the last thing was selected in Smart Suggest. This is used
     * to prevent double-clicks, i.e. selects that happen in quick succession and are almost certainly
     * unintended.
     */
    lastSelectTime: undefined,
};

// Module-wide instance of the DI module. This is set during this module's initialization, because it can't be
// specified as a dependencies to avoid circular requiring of incompletely initialized modules.
// eslint-disable-next-line no-unused-vars
let directIntegration;

/**
 * Checks shop config, loads modules and dependencies, initializes autocomplete
 *
 * @param {Object} shopConfig The specific autocomplete config of the shop
 * @param {boolean} isDirectIntegration Whether this shop uses direct integration
 * @param {function} callback The callback function will be called when all init steps are done
 */
FlAutocomplete.init = function (shopConfig, isDirectIntegration, callback) {
    if (this.preventInitialization) {
        logger.info('Prevented initialization of Smart Suggest');

        return;
    }

    this.log = logger;
    this.log.debugMode = shopConfig.debugMode || 0;
    this.shopConfig = shopConfig;
    this.shopSystem = ShopSystem.forConfig(shopConfig);
    this.isDirectIntegration = isDirectIntegration;
    this.isMobile = FlUtil.isMobile(this.shopConfig);
    this.shopConfig.searchFieldElement = FlUtil.getSearchFieldElements();
    this.lastSelectTime = 0;
    this.usergrouphash = identity.getUsergrouphash();

    if (this.shopConfig.debugMode > 0) {
        try {
            this.checkConfig(this.shopConfig);
        } catch (e) {
            throw e.message;
        }
    }

    // Load autocomplete css files
    // If none is given, then the client may have its own one
    const cssFile = this.shopConfig.cssFile;
    if (cssFile) {
        if (cssFile instanceof Array) {
            jQuery.each(cssFile, function (i, cssUrl) {
                frontend.loadCss(cssUrl, 'fl-smart-suggest-css');
            });
        } else {
            frontend.loadCss(cssFile, 'fl-smart-suggest-css');
        }
    } else {
        logger.warn('No CSS file specified in FINDOLOGIC autocompleteConfig');
    }

    this.initAutocomplete.call(this);
    this.triggerAutocompleteReady();

    if (typeof callback === 'function') {
        callback();
    }
};

/**
 * Triggers the flAutocompleteReady event
 */
FlAutocomplete.triggerAutocompleteReady = function () {
    if (this.preventInitialization) {
        logger.info('Prevented initialization of Smart Suggest');

        return;
    }

    const searchFieldElement = this.shopConfig.searchFieldElement;
    this.shopSystem.searchFieldElement = searchFieldElement;

    const readyEvent = new CustomEvent(this.READY_EVENT_NAME, {
        detail: {
            flAutocomplete: this,
            // (see http://learn.jquery.com/jquery-ui/widget-factory/widget-method-invocation/)
            flAutocompleteUiWidget: searchFieldElement.data('custom-flAutocomplete'),
            autocompleteUlElement: searchFieldElement.flAutocomplete('widget'),
            fljQuery: jQuery,
        },
    });

    document.dispatchEvent(readyEvent);

    return this;
};

/**
 * Checks the config for necessary attributes
 *
 * @param {Object} shopConfig The shopConfig as object (json)
 * @throws Error when a required attribute is not included in the shopConfig object
 */
FlAutocomplete.checkConfig = function (shopConfig) {
    if (!shopConfig) {
        throw this.log.error('FlAutocomplete: shop config is missing.');
    }

    const requiredConfigAttributes = ['searchFieldSelector', 'autocompleteType', 'autocompleteProxy'];

    for (let i = 0; i < requiredConfigAttributes.length; i++) {
        const attribute = requiredConfigAttributes[i];

        if (!shopConfig.hasOwnProperty(attribute)) {
            throw this.log.error(`FlAutocomplete: ${attribute} attribute missing in shop config.`);
        }
    }
};

/**
 * Initializes the jquery-Ui autocomplete script and
 * set properties for current ShopSystem
 */
FlAutocomplete.initAutocomplete = function () {
    if (this.preventInitialization) {
        logger.info('Prevented initialization of Smart Suggest');

        return;
    }

    const searchFieldElement = FlAutocomplete.shopConfig.searchFieldElement;
    const autocompleteType = this.shopConfig.autocompleteType;

    this.shopSystem.shopConfig = this.shopConfig;

    searchFieldElement.flAutocomplete({
        delay: 200,
        source(request, response) {
            FlAutocomplete.abort();

            let params = utils.getFormValues(this.element.closest('form'));
            params.q = request.term;
            params.query = request.term;
            params.type = autocompleteType;
            params.timestamp = new Date().getTime();
            params.requestId = identity.createUUID();

            // Set autocompleteBlocks and count parameter if mobile
            if (FlAutocomplete.isMobile) {
                params.autocompleteblocks = [FlAutocomplete.SUGGEST_AUTOCOMPLETE_BLOCK];
                params.count = 5;
            }

            let endpoint = FlAutocomplete.shopConfig.autocompleteProxy;
            params.usergrouphash = FlAutocomplete.usergrouphash;

            // Autocomplete explicitly needs the shopkey when running without the proxy.
            if (FlAutocomplete.shopConfig.useAutocompleteProxy === false) {
                params.shopkey = FlAutocomplete.shopConfig.shopkey;

                /*
                 * Add the callback parameter so jQuery does a JSONP request. This cannot be done in the init as
                 * the Guided Shopping (Wizard) also uses the FlAutocomplete.shopConfig.autocompleteProxy, and it
                 * has to add the callback parameter in another way
                 */
                endpoint += '?callback=?';
            }

            // Allow shop system-specific request parameter modification.
            params = FlAutocomplete.shopSystem.transformSmartSuggestRequest(params);

            FlAutocomplete.currentAutocompleteRequest = jQuery.getJSON(endpoint, params, response);
        },
        shopSystem: this.shopSystem,
        shopConfig: this.shopConfig,
        isMobile: this.isMobile,

        select(event, ui) {
            // Prevent accidental double clicks from triggering multiple searches.
            const now = new Date();
            if (now - FlAutocomplete.lastSelectTime > FlAutocomplete.INTERACTION_COOLDOWN_MS) {
                FlAutocomplete.lastSelectTime = now;
                FlAutocomplete.selectItem(event, ui);
            }
        },
        focus(event, ui) {
            FlAutocomplete.handleKeyEventOnItemSelect.call(FlAutocomplete, event, ui.item);
        },
        close() {
            FlAutocomplete.reset();
        },
        position: { my: 'left top', at: 'left bottom', collision: 'flip none' },
        open(event, ui) {
            if (FlAutocomplete.isMobile) {
                FlAutocomplete.initializeSuggestIcons();
            }
            FlAutocomplete.bindEvents.call(FlAutocomplete, event, ui);
            FlAutocomplete.afterRendering.call(FlAutocomplete, event, ui);
        },
    });

    searchFieldElement.parents('form').submit(function () {
        FlAutocomplete.abort();
    });

    if (FlAutocomplete.isMobile) {
        // When focusing the searchField on mobile scroll to the field to have a view at the smart suggest
        searchFieldElement.focus(function () {
            const offset = jQuery(this).offset();
            jQuery('html, body').animate({
                // The searchField should not stick at the top of the screen, therefore subtract 10px
                scrollTop: offset.top - 10,
            });
        });
    } else {
        // On desktop, show the previous results when focusing the search field again
        searchFieldElement.focus(function () {
            jQuery(this).flAutocomplete('search');
        });
    }

    // Initial adjustments necessary for certain shop systems are now made according to the shop system-specific
    // module.
    FlAutocomplete.shopSystem.init();
};

/**
 * Called when autocomplete result was rendered
 *
 * @param {Event} event
 * @param {Object} ui The ui object is empty but included for consistency with other events.
 */
// eslint-disable-next-line no-unused-vars
FlAutocomplete.afterRendering = function (event, ui) {
    // overwrite this method if you feel happy
};

/**
 * Attach event handler to elements
 *
 * @param {Event} event
 * @param {Object} uiItem The ui object is empty but included for consistency with other events.
 */
FlAutocomplete.bindEvents = function (event) {
    const shopSystem = this.shopSystem;
    const currentForm = FlAutocomplete.getFormByEventTarget(event);

    shopSystem.bindImageHoverEvent();
    shopSystem.bindShowImageLoadingSpinner();

    // Bind Handler for "All-result item"
    const allResultObj = jQuery('.ui-autocomplete-allResults');

    // Call off() method, to prevent multiple binding and triggering
    allResultObj.off('click').on('click', function () {
        shopSystem.submitForm(currentForm);
    });

    // Hide when searching using only the text field.
    currentForm.submit(function () {
        jQuery('.ui-autocomplete').hide();

        // Blur searchField to hide keyboard on mobile devices after submit
        if (FlAutocomplete.isMobile) {
            FlAutocomplete.shopConfig.searchFieldElement.blur();
        }
    });
};

/**
 * Replace whatever vector graphic img tags we have with the SVG markup
 */
FlAutocomplete.initializeSuggestIcons = function () {
    const injectorOptions = {
        each(svg) {
            // Add click-handler for "Suggestion-icon" in mobile-view
            jQuery(svg).click(function () {
                const searchFieldElement = FlAutocomplete.shopConfig.searchFieldElement;
                let suggestion = jQuery(this);
                if (FlAutocomplete.shopConfig.autocompleteType === 'result_v3') {
                    suggestion = suggestion.siblings('.fl-product-info-wrapper').find('.fl-title').text();
                } else {
                    suggestion = suggestion.siblings('.fl-title').text();
                }

                // Sets the suggestion as value of the input
                // Adds an extra space for the user if they want to continue the search input.
                jQuery(searchFieldElement).val(`${suggestion} `);
                jQuery(searchFieldElement).flAutocomplete('search');

                return false;
            });
        },
    };

    SvgInjector(jQuery(FlAutocomplete.SUGGESTION_ICON_CLASS), injectorOptions);
};

/**
 * Cancels the currently ongoing autocomplete request, if any. Call this once the search is submitted to prevent
 * suggestions from being shown after there are already results.
 */
FlAutocomplete.abort = function () {
    if (typeof FlAutocomplete.currentAutocompleteRequest !== 'undefined') {
        FlAutocomplete.currentAutocompleteRequest.abort();
        FlAutocomplete.currentAutocompleteRequest = undefined;
        jQuery('.ui-autocomplete-loading').removeClass('ui-autocomplete-loading');
    }
};

FlAutocomplete.reset = function () {
    this.shopSystem.reset();
};

/**
 * Tracks Smart Suggest clicks using a tracking service available in the shop. If none is available, nothing
 * will happen.
 *
 * @param {string} query The entered query that led to the suggestions.
 * @param {{}} item The data of the selected item.
 * @param {string} item.block (Internal) name of the block the suggestion appeared in.
 * @param {string} item.label Visible text of the selected item.
 * @param {string} [item.productPlacement] Name of the product placement that caused the selected item to
 * be displayed, if applicable.
 * @param {string} [item.ordernumber] The ordernumber that caused the selected item to be displayed,
 * if applicable.
 * @param {function} callback Called once tracking is complete.
 */
// Refactoring old code that is on the way out just isn't worth it.
// eslint-disable-next-line complexity
FlAutocomplete.trackClick = function (query, item, callback) {
    const tracker = Tracker.getInstance(this.shopConfig, this.shopConfig.trackSmartSuggest);

    let label = item.label;
    let readableBlockName;
    let trackingQuery;
    switch (item.block) {
        case 'suggest':
            readableBlockName = 'Suggestion';
            break;
        case 'cat':
            readableBlockName = 'Category';

            // Replace the category delimiter with something more person-friendly.
            label = label.replace(/_/g, ' → ');
            break;
        case 'vendor':
            readableBlockName = 'Brand';
            break;
        case 'landingpage':
            readableBlockName = 'Landingpage';
            trackingQuery = label;
            break;
        case 'promotion':
            readableBlockName = 'Promotion';
            trackingQuery = label;
            break;
        case 'product':
            // Differentiate between products suggested because of product placement, ordernumber, and title.
            if (item.productPlacement) {
                readableBlockName = 'Product Placement';
                trackingQuery = label;
            } else if (item.ordernumber) {
                readableBlockName = 'Ordernumber';
                trackingQuery = item.ordernumber;
            } else {
                readableBlockName = 'Product';
                trackingQuery = label;
            }

            break;
        default:
            readableBlockName = item.block;
    }

    // Is decremented in each tracking event (event & search to make sure that everything executed before the
    // callback.
    let trackingCallbackCount = 2;

    function decrementTrackingCallbackCountAndExecuteCallback() {
        trackingCallbackCount--;
        if (trackingCallbackCount === 0) {
            callback();
        }
    }

    const category = `Smart Suggest Click: ${readableBlockName}`;
    // Needed to be able to specify the action for trackEvent. Since the frontend delivers the blocks
    // htmlencoded, we decode them back for GA.
    const action = trackingQuery !== undefined ? jQuery.parseHTML(trackingQuery)[0].data : label;

    tracker.trackEvent('findologicEvent', category, action, query, decrementTrackingCallbackCountAndExecuteCallback, {
        event: EventNames.SMART_SUGGEST_CLICK,
        block: item.block,
        query,
    });
    if (trackingQuery !== undefined) {
        const name = this.shopConfig.searchFieldElement[0].getAttribute('name');
        tracker.trackSearch(`${name}=${action}`, undefined, decrementTrackingCallbackCountAndExecuteCallback);
    } else {
        decrementTrackingCallbackCountAndExecuteCallback();
    }
};

/**
 * Called when clicking on an item
 *
 * @param {Event} event
 * @param {Object} ui Contains the selected item
 */
FlAutocomplete.selectItem = function (event, ui) {
    const self = this;
    const item = ui.item;
    const searchTerm = event.target.value;
    const shopSystem = this.shopSystem;
    const currentForm = FlAutocomplete.getFormByEventTarget(event);

    // Set searchFieldElement to event.target, to ensure that the correct input is submitted
    if (jQuery(event.target).is('input')) {
        this.shopConfig.searchFieldElement = jQuery(event.target);
    } else {
        throw new Error('event.target is not an input-field. Cannot set the correct searchFieldElement');
    }

    // Suggestions with a URL should trigger a DI request before that URL is navigated to.
    if (item.url) {
        // Adapt the product URL, if this item is located in the product block. Most shop systems won't
        // care. PlentyMarkets does.
        if (item.block === shopSystem.BLOCKS.PRODUCT) {
            item.url = shopSystem.fixProductUrl(FlAutocomplete.shopConfig, item.url);
        }

        shopSystem.appendSpecificItemsToForm(ui.item, currentForm, self.shopConfig.autocompleteType);

        // Trigger a search request so the interaction gets logged.
        const request = new Request({
            timeout: 1000,
            onComplete() {
                FlAutocomplete.trackClick(searchTerm, item, function () {
                    shopSystem.followUrl(item.url);
                });
            },
        });
        request.send(Request.TYPE_SEARCH, {});
    } else {
        FlAutocomplete.trackClick(searchTerm, item, function () {
            // Items without URLs should just submit the search form.
            shopSystem.appendItemsAndSubmitForm(ui.item, currentForm, self.shopConfig.autocompleteType);
        });
    }
};

/**
 * Triggered when item gets focus (either key or mouse)
 *
 * @param {Object} event The event object
 * @param {item} item The item which got the focus
 */
FlAutocomplete.handleKeyEventOnItemSelect = function (event, item) {
    // Cat Items have a label with underscores.
    // We dont want to show those underscores in the search field value
    if (event.hasOwnProperty('key') && item.block === this.shopSystem.BLOCKS.CAT) {
        const self = this;

        /*
     Because jquery-ui triggers first the focus event and then sets the input value,
     we have to force the overwritting by a delay
     */
        setTimeout(function () {
            const searchField = self.shopConfig.searchFieldElement;
            const underscoreLessValue = searchField.val().replace(/\_/g, ' -> ');

            self.shopConfig.searchFieldElement.val(underscoreLessValue);
        }, 10);
    }
};

/**
 * Get the currently used form to be sure to submit the correct one
 *
 * @param event
 * @returns {Object}
 */
FlAutocomplete.getFormByEventTarget = function (event) {
    let visibleForm;
    const eventTarget = jQuery(event.target);

    if (eventTarget.is('input')) {
        visibleForm = eventTarget.closest('form');
    } else {
        throw new Error('event.target is not an input-field. Cannot find the associated form');
    }

    return visibleForm;
};

export default FlAutocomplete;
